import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { fmtDate, fmtMoney, todayStr, daysBetween } from '../lib/format.js';
import { CATEGORY_ICON } from '../lib/constants.js';
import { ComplianceCreateModal } from './ComplianceCreateModal.jsx';
import { ComplianceReminderDetailModal } from './ComplianceReminderDetailModal.jsx';
import { DocumentUploadModal } from './DocumentUploadModal.jsx';
import { docIcon } from '../lib/ui.js';

// ─── ComplianceView ───────────────────────────────────────────────────────────
export function ComplianceView({ user, location, locations }) {
  const [reminders, setReminders] = React.useState([]);
  const [documents, setDocuments] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [activeSubTab, setActiveSubTab] = React.useState('dashboard');
  const [showCreate, setShowCreate] = React.useState(false);
  const [editReminder, setEditReminder] = React.useState(null);
  const [selectedReminder, setSelectedReminder] = React.useState(null);
  const [showDocUpload, setShowDocUpload] = React.useState(false);
  const [docSearch, setDocSearch] = React.useState('');
  const [docCatFilter, setDocCatFilter] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  const isOwner = window.EmpireRoles.isOwner(user);

  React.useEffect(() => {
    api('GET', '/api/users').then(setUsers).catch(() => {});
    setLoading(true);
    loadReminders();
    loadDocuments();
  }, [location?.id]);

  // Never raise `loading` here: the placeholder collapses the page and resets scroll.
  async function loadReminders() {
    try {
      const data = await api('GET', `/api/compliance/reminders?location_id=${location.id}`);
      setReminders(data);
    } catch {}
    setLoading(false);
  }

  async function loadDocuments() {
    try {
      const data = await api('GET', `/api/compliance/documents?location_id=${location.id}`);
      setDocuments(data);
    } catch {}
  }

  // Filter reminders for this user
  function canView(r) {
    if (isOwner) return true;
    if (r.shared_with === 'all_managers') return true;
    try {
      const arr = JSON.parse(r.shared_with);
      return Array.isArray(arr) && arr.includes(user.id);
    } catch { return false; }
  }

  const today = todayStr();
  const visible = reminders.filter(canView);
  // CJ 2026-05-16: show ALL items by date, not just next 30 days. The
  // dashboard now buckets Overdue / Due-within-30 / Upcoming (everything
  // 30+ days out), all sorted soonest → latest.
  const byDateAsc = (a, b) => (a.due_date || '').localeCompare(b.due_date || '');
  const overdue  = visible.filter(r => r.status === 'active' && r.due_date < today).sort(byDateAsc);
  const dueSoon  = visible.filter(r => r.status === 'active' && r.due_date >= today && daysBetween(today, r.due_date) <= 30).sort(byDateAsc);
  const upcoming = visible.filter(r => r.status === 'active' && r.due_date >= today && daysBetween(today, r.due_date) > 30).sort(byDateAsc);
  const done     = visible.filter(r => r.status === 'completed');

  async function handleDelete(id) {
    await api('DELETE', `/api/compliance/reminders/${id}`);
    setSelectedReminder(null);
    loadReminders();
  }

  async function handleDownloadDoc(doc) {
    try {
      const result = await api('GET', `/api/compliance/documents/${doc.id}/download`);
      const link = document.createElement('a');
      link.href = result.file_data;
      link.download = doc.name;
      link.click();
    } catch(e) { alert('Download failed'); }
  }

  async function handleDeleteDoc(docId) {
    if (!confirm('Delete this document?')) return;
    await api('DELETE', `/api/compliance/documents/${docId}`);
    loadDocuments();
  }

  const filteredDocs = documents.filter(d => {
    const matchSearch = !docSearch || d.name.toLowerCase().includes(docSearch.toLowerCase());
    const matchCat = !docCatFilter || d.category === docCatFilter;
    return matchSearch && matchCat;
  });

  function ReminderSection({ title, items, accentClass }) {
    if (!items.length) return null;
    return (
      <div>
        <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${accentClass}`}>{title} ({items.length})</h3>
        <div className="space-y-2">
          {items.map(r => <ReminderCard key={r.id} reminder={r} onClick={() => setSelectedReminder(r)} today={today} />)}
        </div>
      </div>
    );
  }

  function ReminderCard({ reminder, onClick, today }) {
    const daysUntil = daysBetween(today, reminder.due_date);
    const isOver = daysUntil < 0;
    let lease = null;
    try { lease = reminder.lease_details ? JSON.parse(reminder.lease_details) : null; } catch {}

    return (
      <div onClick={onClick}
        className={`bg-white border rounded-xl p-4 cursor-pointer hover:shadow-md transition-all ${isOver ? 'border-red-200' : daysUntil <= 30 ? 'border-amber-200' : 'border-gray-100'}`}>
        <div className="flex items-start gap-3">
          <span className="text-2xl flex-shrink-0 mt-0.5">{CATEGORY_ICON[reminder.category] || '📌'}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-ink text-sm">{reminder.title}</p>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                isOver ? 'bg-red-100 text-red-700' : daysUntil <= 30 ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}>
                {isOver ? `${Math.abs(daysUntil)}d overdue` : `${daysUntil}d`}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className="text-xs text-gray-400">{fmtDate(reminder.due_date)}</span>
              <span className="text-xs text-gray-300">·</span>
              <span className="text-xs text-gray-400 capitalize">{reminder.recurrence}</span>
              {reminder.shared_with === 'all_managers' && <span className="text-xs text-blue-500">All Managers</span>}
            </div>
            {lease && lease.monthly_rent && (
              <div className="mt-2 flex gap-4 text-xs text-gray-500">
                <span>Rent: <b className="text-ink">{fmtMoney(lease.monthly_rent)}/mo</b></span>
                {lease.annual_increase != null && <span>Next yr: <b className="text-ink">{fmtMoney(Math.round(lease.monthly_rent * (1 + lease.annual_increase / 100)))}/mo</b></span>}
                {lease.notice_period && <span>Notice: <b className="text-ink">{lease.notice_period}d</b></span>}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {selectedReminder && (
        <ComplianceReminderDetailModal
          reminder={reminders.find(r => r.id === selectedReminder.id) || selectedReminder}
          user={user} users={users} location={location} isOwner={isOwner}
          onClose={() => setSelectedReminder(null)}
          onComplete={() => { loadReminders(); setSelectedReminder(null); }}
          onEdit={r => { setEditReminder(r); setSelectedReminder(null); setShowCreate(true); }}
          onDelete={handleDelete}
          onDocUploaded={loadDocuments}
        />
      )}
      {showCreate && (
        <ComplianceCreateModal
          reminder={editReminder} locations={locations} users={users} currentUser={user}
          onClose={() => { setShowCreate(false); setEditReminder(null); }}
          onSave={() => { setShowCreate(false); setEditReminder(null); loadReminders(); }}
        />
      )}
      {showDocUpload && (
        <DocumentUploadModal location={location} user={user}
          onClose={() => setShowDocUpload(false)}
          onSave={() => { setShowDocUpload(false); loadDocuments(); }}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          {['dashboard', 'documents'].map(tab => (
            <button key={tab} onClick={() => setActiveSubTab(tab)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${activeSubTab === tab ? 'bg-white text-ink shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {tab === 'dashboard' ? '📋 Compliance' : '📁 Documents'}
            </button>
          ))}
        </div>
        {isOwner && activeSubTab === 'dashboard' && (
          <button onClick={() => { setEditReminder(null); setShowCreate(true); }}
            className="bg-crimson text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-crimson-dark">
            + Add Reminder
          </button>
        )}
        {activeSubTab === 'documents' && (
          <button onClick={() => setShowDocUpload(true)}
            className="bg-gold text-ink text-sm px-4 py-2 rounded-lg font-medium hover:bg-gold-light">
            + Upload Doc
          </button>
        )}
      </div>

      {/* Dashboard sub-tab */}
      {activeSubTab === 'dashboard' && (
        loading ? (
          <div className="flex items-center justify-center h-40 text-gray-400">Loading...</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">📋</p>
            <p className="font-medium">No compliance reminders</p>
            {isOwner && <p className="text-sm mt-1">Use + Add Reminder to create one</p>}
          </div>
        ) : (
          <div className="space-y-6">
            <ReminderSection title="🔴 Overdue" items={overdue} accentClass="text-red-600" />
            <ReminderSection title="🟡 Due Within 30 Days" items={dueSoon} accentClass="text-amber-600" />
            <ReminderSection title="📅 Upcoming" items={upcoming} accentClass="text-sky-600" />
            {(!overdue.length && !dueSoon.length && !upcoming.length) && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <p className="text-green-600 font-semibold text-lg">✓ All clear!</p>
                <p className="text-green-500 text-sm">No compliance items due in the next 60 days.</p>
              </div>
            )}
            <ReminderSection title="✅ Recently Completed" items={done.slice(0, 5)} accentClass="text-green-600" />
          </div>
        )
      )}

      {/* Documents sub-tab */}
      {activeSubTab === 'documents' && (
        <div>
          <div className="flex gap-3 mb-4">
            <input value={docSearch} onChange={e => setDocSearch(e.target.value)}
              placeholder="Search documents..." className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-crimson/30" />
            <select value={docCatFilter} onChange={e => setDocCatFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
              <option value="">All Categories</option>
              {['license','permit','insurance','tax_filing','lease','contract','other'].map(c => (
                <option key={c} value={c}>{c.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          {filteredDocs.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-4xl mb-3">📁</p>
              <p className="font-medium">No documents yet</p>
              <p className="text-sm mt-1">Upload a document using the button above</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredDocs.map(doc => {
                const expDays = doc.expiration_date ? daysBetween(today, doc.expiration_date) : null;
                return (
                  <div key={doc.id} className={`bg-white border rounded-xl p-4 flex items-center gap-3 ${expDays != null && expDays < 30 ? 'border-amber-200' : 'border-gray-100'}`}>
                    <span className="text-2xl flex-shrink-0">{docIcon(doc.file_type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-ink text-sm truncate">{doc.name}</p>
                      <div className="flex items-center gap-2 flex-wrap mt-0.5">
                        <span className="text-xs text-gray-400 capitalize">{doc.category?.replace('_', ' ') || 'Other'}</span>
                        <span className="text-xs text-gray-300">·</span>
                        <span className="text-xs text-gray-400">{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                        {doc.expiration_date && (
                          <>
                            <span className="text-xs text-gray-300">·</span>
                            <span className={`text-xs font-medium ${expDays != null && expDays < 30 ? 'text-amber-600' : 'text-gray-400'}`}>
                              Expires {fmtDate(doc.expiration_date)}{expDays != null && expDays < 30 ? ` (${expDays}d)` : ''}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <button onClick={() => handleDownloadDoc(doc)}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex-shrink-0 px-3 py-1.5 border border-blue-200 rounded-lg">⬇ Download</button>
                    {isOwner && (
                      <button onClick={() => handleDeleteDoc(doc.id)}
                        className="text-xs text-red-400 hover:text-red-600 flex-shrink-0 ml-1">✕</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
